// Considered: staying self-contained — rejected because GlyphAtom is the
// convergence primitive that handles Iconify and letter rendering uniformly.
// ScopeCoinAtom becomes a thin coin-shaped wrapper. See spec Reuse Audit.
import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './ScopeCoinAtom.scss';

export type ScopeCoinScope = 'center' | 'organization' | 'metasphere' | 'all';
export type ScopeCoinSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type ScopeCoinGlyphKind = 'letter' | 'icon';

export interface ScopeCoinAtomProps {
  scope: ScopeCoinScope;
  glyph?: string | null;
  channelName: string;
  size?: ScopeCoinSize;
  /**
   * Controls how the `glyph` prop is rendered.
   * - `'icon'` (default): delegates to GlyphAtom which renders the Iconify
   *   `set:name`; falls back to the `channelName` initial when glyph is absent
   *   or unrecognised.
   * - `'letter'`: treats `glyph` as a display character (e.g. the publisher's
   *   brand initial). Any non-empty string is rendered as text.
   */
  glyphKind?: ScopeCoinGlyphKind;
  /**
   * When true, render the system-channel shield icon regardless of the glyph or
   * channelName props. Used on the channel-detail title row to mark
   * Center-managed system channels per design `v2-detail.jsx:368`.
   */
  isSystem?: boolean;
  /**
   * When true, renders a small badge-check overlay in the bottom-right corner
   * of the coin to indicate an officially verified publisher.
   */
  isOfficial?: boolean;
  /**
   * Publisher brand hue (0–359). When provided, overrides the scope gradient
   * via a CSS custom property so the coin tints to the publisher's brand colour.
   */
  hue?: number;
  className?: string;
}

const SYSTEM_GLYPH = 'lucide:shield';

// Valid icon pattern for glyphKind='icon': Iconify 'set:name'.
// Unrecognised strings fall back to the channelName initial (matching old behaviour).
const ICON_GLYPH_SHAPE = /^[a-z0-9-]+:[a-z0-9-]+$/i;

function toValidIconGlyph(g: string | null | undefined): string | null | undefined {
  if (!g) return g;
  return ICON_GLYPH_SHAPE.test(g) ? g : null;
}

const ScopeCoinAtom: React.FC<ScopeCoinAtomProps> = ({
  scope,
  glyph,
  channelName,
  size = 'md',
  glyphKind = 'icon',
  isSystem = false,
  isOfficial = false,
  hue,
  className = '',
}) => {
  const classes = [
    'marketplace-scope-coin',
    `marketplace-scope-coin--${scope}`,
    `marketplace-scope-coin--${size}`,
    hue !== undefined ? 'marketplace-scope-coin--custom-hue' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const style = hue !== undefined
    ? ({ '--scope-coin-custom-hue': `${hue}` } as React.CSSProperties)
    : undefined;

  const hasExplicitGlyph = glyph !== null && glyph !== undefined && glyph !== '';
  // System channels fall back to the shield only when they declare no glyph of their own.
  const useSystemShield = isSystem && !hasExplicitGlyph;
  const rawGlyph = useSystemShield ? SYSTEM_GLYPH : glyph;
  const effectiveGlyphKind = useSystemShield ? 'icon' : glyphKind;

  // For 'icon' kind, only pass recognised Iconify names (set:name) to GlyphAtom;
  // unrecognised strings fall back to the channelName initial.
  const glyphForAtom = effectiveGlyphKind === 'letter'
    ? rawGlyph
    : toValidIconGlyph(rawGlyph);

  // When glyphKind='letter', pass it through to GlyphAtom to force letter rendering.
  // When 'icon', let GlyphAtom's resolveGlyph auto-detect Iconify vs. fallback.
  const resolvedKind = effectiveGlyphKind === 'letter' ? ('letter' as const) : undefined;

  return (
    <span className={classes} style={style} aria-hidden="true">
      <GlyphAtom
        glyph={glyphForAtom}
        glyphKind={resolvedKind}
        fallbackText={channelName}
      />
      {isOfficial && (
        <span className="marketplace-scope-coin__official-badge" aria-hidden="true">
          <i className="pi pi-verified" />
        </span>
      )}
    </span>
  );
};

ScopeCoinAtom.displayName = 'ScopeCoinAtom';

export default ScopeCoinAtom;
